import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TFasterPayoutStatus } from '../../../../../api/src';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { usePagerPropsFromUrl } from '../pager/use-pager-props-from-url';
import { useToasts } from '../use-toasts';
import {
  isFasterPayoutProcessingStatus,
  useMcFasterPayoutsQuery,
} from './queries';
import { showFasterPayoutStatusToast } from './show-payout-status-toast';

type TrackedPayout = {
  id: number;
  status: TFasterPayoutStatus;
};

export const useMcFasterPayoutStatusToast = () => {
  const { data } = useMcFasterPayoutsQuery();
  const { Page } = usePagerPropsFromUrl();
  const contractId = useURLContractId();
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const { t } = useTranslation(['makeFasterPayout']);
  const trackedRef = useRef<TrackedPayout | null>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (Page !== 1) {
      return;
    }

    const latest = data?.items?.[0];
    if (!latest) {
      if (!data?.items?.length) {
        trackedRef.current = null;
        isInitializedRef.current = false;
      }
      return;
    }

    const tracked = trackedRef.current;
    const isNewLatest = tracked?.id !== latest.id;
    const previousStatus =
      tracked && !isNewLatest ? tracked.status : null;

    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      trackedRef.current = { id: latest.id, status: latest.status };
      return;
    }

    if (
      latest.status === 'Success' &&
      previousStatus !== null &&
      isFasterPayoutProcessingStatus(previousStatus)
    ) {
      showFasterPayoutStatusToast('Success', toasts, t);
      if (contractId) {
        queryClient.refetchQueries({ queryKey: ['balances', contractId] });
        queryClient.refetchQueries({
          queryKey: ['faster-payouts-balances', contractId],
        });
      }
    }

    if (latest.status === 'Rejected') {
      const rejectedAfterProcessing =
        previousStatus !== null &&
        isFasterPayoutProcessingStatus(previousStatus);
      const newRejectedPayout = isNewLatest && tracked !== null;

      if (rejectedAfterProcessing || newRejectedPayout) {
        showFasterPayoutStatusToast('Rejected', toasts, t);
      }
    }

    trackedRef.current = { id: latest.id, status: latest.status };
  }, [Page, contractId, data, queryClient, t, toasts]);
};
